import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import Tooltip from '../../components/utils/Tooltip';

const LinedCanvas = ({ width = 400, height = 400, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [error, setError] = useState('');
  const [selectedButton, setSelectedButton] = useState(null);

  const drawColor = 'black';

  // Draw horizontal lines
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = 'lightgray';
    ctx.lineWidth = 0.5;
    for (let y = 30; y < height; y += 30) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height]);

  // Drawing handlers
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const startDrawing = (x, y) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };

    const draw = (x, y) => {
      if (!isDrawing) return;
      ctx.strokeStyle = drawColor;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath();
      }
    };

    const getMousePos = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const getTouchPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    };

    // Mouse events
    const handleMouseDown = (e) => startDrawing(getMousePos(e).x, getMousePos(e).y);
    const handleMouseMove = (e) => draw(getMousePos(e).x, getMousePos(e).y);
    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;

    // Touch events
    const handleTouchStart = (e) => { e.preventDefault(); startDrawing(getTouchPos(e).x, getTouchPos(e).y); };
    const handleTouchMove = (e) => { e.preventDefault(); draw(getTouchPos(e).x, getTouchPos(e).y); };
    const handleTouchEnd = endDrawing;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing]);

  const handleButtonClick = (buttonName, callback) => {
    setSelectedButton(buttonName);
    if (callback) callback();
  };

  // Clear canvas
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  // Download canvas as image
  const handleDownloadImage = () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL();
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = `canvas_image_${Date.now()}.png`;
    link.click();
  };

  // Download canvas as PDF
  const handleDownloadPDF = () => {
    const canvas = canvasRef.current;
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;
    pdf.addImage(canvas.toDataURL(), 'PNG', 0, 0, imgWidth, imgHeight);
    pdf.save(`canvas_pdf_${Date.now()}.pdf`);
  };

  // Upload canvas image
  const handleUploadImage = async () => {
    try {
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL('image/png');
      const blob = dataURItoBlob(dataURL);
      const formData = new FormData();
      formData.append('image', blob, 'canvas.png');
      await axios.post('http://localhost:5000/upload', formData);
      alert('Image uploaded successfully!');
    } catch {
      alert('Upload failed.');
    }
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: 'image/png' });
  };

  return (
    <div style={{ marginTop: '10px', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{ border: '1px solid #ccc', backgroundColor: 'white', display: 'block', margin: '0 auto' }}
      />
      <div className="flex flex-row justify-center gap-2 px-2 mt-2">
        <Tooltip text="Clear Canvas">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'clear' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('clear', clearCanvas)}
          >
             <i className="fa-solid fa-undo"></i>
          </span>
        </Tooltip>

        <Tooltip text="Upload Image">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'upload' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('upload', handleUploadImage)}
          >
            <i className="fa-solid fa-upload"></i>
          </span>
        </Tooltip>

        <Tooltip text="Download Image">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'downloadImage' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('downloadImage', handleDownloadImage)}
          >
             <i className="fa-solid fa-download"></i>
          </span>
        </Tooltip>

        <Tooltip text="Download PDF">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'downloadPDF' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('downloadPDF', handleDownloadPDF)}
          >
            <i className="fa-solid fa-file-pdf"></i>
          </span>
        </Tooltip>

        <Tooltip text="Close">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'close' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('close', onClose)}
          >
             <i className="fa-solid fa-arrow-right-from-bracket"></i>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default LinedCanvas;
